import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MaterialSymbol } from 'react-material-symbols'
import { Avatar } from '../primitives/Avatar'
import { Modal } from '../primitives/Modal'
import {
  resetLeaveToPending,
  type OverlappingLeaveItem,
} from '../../api/blackouts'
import { fmtDate, fmtDateRange } from '../../lib/format'

export interface ReviewItem extends OverlappingLeaveItem {
  blackoutId: string
  blackoutName: string
  declaredAt: string
}

interface BlackoutReviewBannerProps {
  reviewItems: ReviewItem[]
}

export function BlackoutReviewBanner({
  reviewItems,
}: BlackoutReviewBannerProps): JSX.Element | null {
  const [bannerHidden, setBannerHidden] = useState(false)

  if (reviewItems.length === 0) return null

  // Find the most recently declared blackout among review items
  const latestItem = [...reviewItems].sort(
    (a, b) => b.declaredAt.localeCompare(a.declaredAt),
  )[0]

  return (
    <div
      data-testid="blackout-review-banner"
      style={{
        background: 'var(--warning-bg)',
        border: '1px solid var(--warning-line)',
        borderRadius: 12,
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      {/* Banner header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 18px',
        }}
      >
        <MaterialSymbol icon="warning" size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
            {reviewItems.length} previously approved{' '}
            {reviewItems.length === 1 ? 'leave needs' : 'leaves need'} re-review
          </div>
          {latestItem ? (
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
              Pushed to pending after &ldquo;{latestItem.blackoutName}&rdquo; was declared on{' '}
              {fmtDate(latestItem.declaredAt)}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setBannerHidden((v) => !v)}
          style={{
            padding: '6px 12px',
            border: '1px solid var(--warning-line)',
            borderRadius: 8,
            background: 'transparent',
            color: 'var(--text-2)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {bannerHidden ? 'Show panel' : 'Hide panel'}
        </button>
      </div>

      {/* Review rows */}
      {!bannerHidden ? (
        <div
          style={{
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {reviewItems.map((item) => (
            <ReviewRow key={`${item.leaveId}-${item.blackoutId}`} item={item} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

interface ReviewRowProps {
  item: ReviewItem
}

function ReviewRow({ item }: ReviewRowProps): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [slackOpen, setSlackOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [infoHovered, setInfoHovered] = useState(false)

  const resetMutation = useMutation({
    mutationFn: () => resetLeaveToPending(item.leaveId, item.blackoutId, item.blackoutName),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blackouts'] })
      void queryClient.invalidateQueries({ queryKey: ['leaves'] })
      void queryClient.invalidateQueries({ queryKey: ['blackout-overlapping', item.blackoutId] })
      setConfirmOpen(false)
    },
  })

  const typeLabel =
    item.type.charAt(0).toUpperCase() + item.type.slice(1).replace(/_/g, ' ')

  function handleInfoClick(): void {
    void navigate({ to: '/leave', search: { id: item.leaveId } as never })
  }

  function handleInfoKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      void navigate({ to: '/leave', search: { id: item.leaveId } as never })
    }
  }

  return (
    <div
      data-testid="review-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 18px',
        borderTop: '1px solid var(--border)',
      }}
    >
      {/* Clickable info area: Avatar + name + dates */}
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open leave detail for ${item.employeeName}`}
        onClick={handleInfoClick}
        onKeyDown={handleInfoKeyDown}
        onMouseEnter={() => setInfoHovered(true)}
        onMouseLeave={() => setInfoHovered(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flex: 1,
          minWidth: 0,
          cursor: 'pointer',
          borderRadius: 8,
          padding: '4px 6px',
          margin: '-4px -6px',
          background: infoHovered ? 'var(--surface-2)' : 'transparent',
          transition: 'background 120ms ease',
        }}
      >
        <Avatar name={item.employeeName} size={32} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
            {item.employeeName} · {typeLabel}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
            {fmtDateRange(item.startDate, item.endDate)} · {item.daysCount} days · overlaps{' '}
            <strong style={{ color: 'var(--text)' }}>{item.blackoutName}</strong>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {copied ? (
          <button
            type="button"
            disabled
            style={{
              padding: '6px 12px',
              border: '1px solid var(--sage-line, #86efac)',
              borderRadius: 8,
              background: 'var(--sage-bg, #f0fdf4)',
              color: 'var(--sage-fg, #166534)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'default',
            }}
          >
            Copied ✓
          </button>
        ) : (
          <button
            type="button"
            data-testid="slack-employee"
            onClick={() => setSlackOpen(true)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: 'var(--surface)',
              color: 'var(--text-2)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Slack employee
          </button>
        )}

        <button
          type="button"
          data-testid="route-to-director"
          onClick={() => setConfirmOpen(true)}
          style={{
            padding: '6px 12px',
            border: 'none',
            borderRadius: 8,
            background: 'var(--steel-blue)',
            color: 'var(--surface)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Route to Director
        </button>
      </div>


      {/* Confirm "Route to Director" modal */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Route leave to Director?"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5 }}>
            This will reset{' '}
            <strong style={{ color: 'var(--text)' }}>{item.employeeName}</strong>
            {"'s "}
            {fmtDateRange(item.startDate, item.endDate)} leave back to{' '}
            <strong>Pending</strong>. Their approver will be asked to re-approve it in the context
            of the blackout <em>{item.blackoutName}</em>.
          </p>
          {resetMutation.isError ? (
            <div
              role="alert"
              style={{
                padding: '10px 12px',
                background: 'var(--danger-bg)',
                color: 'var(--danger-fg)',
                border: '1px solid var(--danger-line)',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {resetMutation.error instanceof Error
                ? resetMutation.error.message
                : 'Reset failed'}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={resetMutation.isPending}
              style={{
                padding: '8px 14px',
                background: 'var(--surface)',
                color: 'var(--text-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontWeight: 600,
                cursor: resetMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="confirm-route-to-director"
              disabled={resetMutation.isPending}
              onClick={() => resetMutation.mutate()}
              style={{
                padding: '8px 14px',
                background: resetMutation.isPending
                  ? 'var(--border-strong)'
                  : 'var(--steel-blue)',
                color: 'var(--surface)',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                cursor: resetMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {resetMutation.isPending ? 'Routing…' : 'Route — approver will re-approve'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
